import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { defer, merge, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { AuthenticationService } from '@auth/authentication.service';
import { ActiveAuthenticationStateService } from '@auth/active-authentication.state-service';
import { ActiveUser } from '@auth/state/models/active-user.model';
import { IconComponent } from '@design-system/buttons/icon/icon.component';
import { TestIdDirective } from '@design-system/core/test-id.directive';
import { BodyComponent } from '@design-system/typography/body/body.component';
import { BodyExtraSmallComponent } from '@design-system/typography/body-xs/body-extra-small.component';

export interface UserMenuProfile {
  email: string;
  initials: string;
}

@Component({
  selector: 'app-user-menu',
  templateUrl: './user-menu.component.html',
  styleUrls: ['./user-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, IconComponent, TestIdDirective, BodyComponent, BodyExtraSmallComponent],
})
export class UserMenuComponent {
  private readonly auth = inject(ActiveAuthenticationStateService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly cdr = inject(ChangeDetectorRef);

  menuOpen = false;

  readonly profile$: Observable<UserMenuProfile>;

  constructor() {
    const activeIdentity$: Observable<ActiveUser | undefined> = merge(
      defer(() => of(this.auth.activeIdentity)),
      this.auth.activeIdentity$
    );
    this.profile$ = activeIdentity$.pipe(
      map((user): UserMenuProfile => this.buildProfile(user ?? undefined)),
      distinctUntilChanged(
        (a, b) => a.email === b.email && a.initials === b.initials
      ),
      shareReplay(1)
    );
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.menuOpen) {
      return;
    }
    const target = event.target as Node | null;
    if (target && this.elementRef.nativeElement.contains(target)) {
      return;
    }
    this.menuOpen = false;
    this.cdr.markForCheck();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.menuOpen) {
      this.menuOpen = false;
      this.cdr.markForCheck();
    }
  }

  toggleMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.menuOpen = !this.menuOpen;
    this.cdr.markForCheck();
  }

  signOut(): void {
    this.menuOpen = false;
    this.authenticationService.signOutAndRedirect();
  }

  private buildProfile(user: ActiveUser | undefined): UserMenuProfile {
    const email = (user?.email || '').trim();
    return {
      email,
      initials: this.computeInitials(user, email),
    };
  }

  private computeInitials(user: ActiveUser | undefined, email: string): string {
    const fore = (user?.firstName ?? '').trim();
    const sur = (user?.lastName ?? '').trim();
    if (fore && sur) {
      return `${fore[0]}${sur[0]}`.toUpperCase();
    }
    if (fore.length >= 2) {
      return fore.slice(0, 2).toUpperCase();
    }
    if (fore.length === 1) {
      return fore.toUpperCase();
    }
    return this.initialsFromEmail(email);
  }

  private initialsFromEmail(email: string): string {
    const local = email.split('@')[0] ?? '';
    const parts = local.split(/[._-]+/).filter((p) => p.length > 0);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    if (local.length >= 2) {
      return local.slice(0, 2).toUpperCase();
    }
    if (local.length === 1) {
      return local.toUpperCase();
    }
    return '?';
  }
}
